import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { path, pathOr } from 'ramda';
import axios from 'axios';

import classNames from 'classnames/bind';

// Utils
import { get as getCookie, setCookie } from 'utils/cookies';
import detectMobileOS from 'utils/detectMobileOS';
import { SUBSCRIPTION_STATUSES, getStatusFromLocalStorage } from 'utils/partnershipTariffStatus';

// Containers/Components
import {
  DataBlocks,
  Preloader,
  MyBeelineBanner,
  MarkBanner,
  Link,
} from 'components';

// App action
import { fetchPersonalData } from 'actions';

import styles from './styles.pcss';
import BonusesBanner from './components/BonusesBanner';
import MPBanner from './components/MPBanner';
import SimTransferRequestStatus from './containers/SimTransferRequestStatus';
import AccountStatus from './containers/AccountStatus';
import PersonalDataStatus from './containers/PersonalDataStatus';
import PostpaidAccountInfo from './containers/PostpaidAccountInfo';
import Summary from './containers/Summary';
import Balance from './containers/Balance';
import Payments from './containers/Payments';
import PostpaidBalance from './containers/PostpaidBalance';
import Accumulators from './containers/Accumulators';
import PartnershipTariff from './containers/PartnershipTariff';
import Convergence from './containers/Convergence';
import WarrantyPayments from './containers/WarrantyPayments';
import BundlesSharing from './containers/BundlesSharing';
import Services from './containers/Services';
import AdditionalBalances from './containers/AdditionalBalances';
import ChangeStatusPopups from './containers/ChangeStatusPopups';
import AccumulatorsSpeedUpPopup from './containers/AccumulatorsSpeedUpPopup';
import MnpStatus from './containers/MnpStatus';
import IPoE from './containers/IPoE';
import MulticonvergenceOfferPlane from './containers/MulticonvergenceOfferPlane';
import EcoRoaming from './containers/EcoRoaming';
import { FamilyWithBeeline } from './containers/FamilyWithBeeline';
import ValidationPDStatus from './containers/ValidationPDStatus';
import ValidationPDPopup from './containers/ValidationPDPopup';
import YouthTariff from './containers/YouthTariff';
import UsisResultPopup from './containers/UsisResultPopup';
import TariffsBlock from './containers/TariffsBlock';

import { withViewAnalytics } from '../../utils/hocs/withViewAnalytics';

// Actions
import {
  fetchInitialData,
  setPartnershipTariffStatus,
  openUsisResultPopup,
  setUsisResultStep,
  fetchTariffsBlockData,
} from './actions';

const cx = classNames.bind(styles);

const { AB_SHOW_MARK_BANNER } = window;

class Home extends Component {
  usisCookieName = '_usis_waiting_3871';

  constructor(props) {
    super(props);

    this.hasSubscriptionPending = createRef(false);

    this.state = {
      externalStore: null,
      isMobile: false,
      tariffInfo: {},
      isTariffShown: true,
    };
  }

  componentDidMount() {
    this.props.actions.fetchInitialData();
    this.props.actions.fetchPersonalData();
    this.props.actions.fetchTariffsBlockData();

    this.interval = setInterval(() => {
      const externalStore = path(['beeline', 'store'], window);
      if (externalStore) {
        this.setState({ externalStore });
        clearInterval(this.interval);
      }
    }, 50);

    if (window && window.location.search && getCookie(this.usisCookieName) === '1') {
      const status = new URLSearchParams(window.location.search).get('status');
      this.props.actions.openUsisResultPopup();
      this.props.actions.setUsisResultStep(status);

      if (status) {
        setCookie({
          name: this.usisCookieName,
          value: 0,
          maxAge: 0,
        });
      }
    }

    this.setState({ isMobile: !!detectMobileOS() });
  }

  componentDidUpdate(prevProps) {
    if (this.props.partnershipTariff && prevProps.partnershipTariff !== this.props.partnershipTariff) {
      const getSubscriptionInfo = async () => {
        try {
          const res = await axios
            .get('/api/yandex-tariff-subscription/', {
              params: {
                redirectUrl: `${window.location.pathname}${window.location.hash}`,
              },
            });

          // Показ лоадера подключения услуги в ЛК и карточке тарифа
          const isSubscriptionConnecting = SUBSCRIPTION_STATUSES.pending === getStatusFromLocalStorage();
          const isAvaliable = SUBSCRIPTION_STATUSES.available === res?.data?.status;
          this.hasSubscriptionPending.current = isSubscriptionConnecting && isAvaliable;
          if (isSubscriptionConnecting && !isAvaliable) {
            localStorage.removeItem('yaPlusSbscriptionStatus');
          }

          this.setState({ tariffInfo: res?.data });
          const isConnected = SUBSCRIPTION_STATUSES.connected === res?.data?.status;
          this.props.actions.setPartnershipTariffStatus(isConnected);
        } catch (e) {
          this.setState({ isTariffShown: false });
          console.error(e);
        }
      };
      getSubscriptionInfo();
    }
  }

  render() {
    const {
      isRestrictedAccess,
      isUserInfoFetching,
      isPostpaid,
      isConvergent,
      isPorted,
      tariffServices,
      bannerData,
      settings,
      sosService,
      hasSimTransferRequest,
      ctn,
      partnershipTariff,
      isUserActive,
      familyWithBeelineError,
      isVisibleFamily,
      familyFeatureToggle,
    } = this.props;
    const {
      externalStore,
      isMobile,
      tariffInfo,
      isTariffShown,
    } = this.state;

    if (isUserInfoFetching) {
      return (
        <Preloader size="80" />
      );
    }

    // Content for departed subscribers
    if (isPorted) {
      return (
        <DataBlocks.Container>
          {bannerData && (
            <MPBanner bannerData={bannerData} />
          )}
          <Summary />
          <Balance />
        </DataBlocks.Container>
      );
    }

    return (
      <>
        <DataBlocks.Container>
          <UsisResultPopup />
          {bannerData && <MPBanner bannerData={bannerData} />}

          {sosService?.data?.text ? (
            <DataBlocks.Block className={cx('text')}>
              <div className={cx('text-content')}>
                <div dangerouslySetInnerHTML={{ __html: sosService.data.text }} />
                {sosService.data.button && (
                  <Link href={sosService.data.button.url}>
                    {sosService.data.button.title}
                  </Link>
                )}
              </div>
            </DataBlocks.Block>
          ) : (
            <AccountStatus />
          )}

          {isConvergent && <IPoE />}
          {isConvergent && <ValidationPDStatus />}
          {hasSimTransferRequest && <SimTransferRequestStatus />}

          <PersonalDataStatus />
          <MnpStatus />

          <MulticonvergenceOfferPlane />

          {isPostpaid && (
            <>
              <PostpaidAccountInfo />
              <PostpaidBalance />
            </>
          )}
          {settings.homePageBanner && (
            <BonusesBanner
              {...settings.homePageBanner}
            />
          )}

          <Summary />

          {AB_SHOW_MARK_BANNER ? (
            <MarkBanner />
          ) : (
            <MyBeelineBanner
              text={`Баланс, услуги, детализация
              — все функции личного кабинета есть в уютном мобильном приложении «Мой билайн»!`}
              images={{
                iOS: '//static.beeline.ru/upload/images/mlk/images/mpBanner/ios-gigi.png',
                Android: '//static.beeline.ru/upload/images/mlk/images/mpBanner/android-gigi.png',
              }}
            />
          )}

          {isConvergent ? (
            <Payments />
          ) : (
            <>
              {(isMobile && tariffServices?.data?.content?.yonth) && <YouthTariff />}
              <Balance />
            </>
          )}

          {partnershipTariff && isTariffShown && (
            <PartnershipTariff
              data={partnershipTariff}
              subscriptionInfo={tariffInfo}
              subscriptionDisabled={!isUserActive}
              hasSubscriptionPending={this.hasSubscriptionPending.current}
            />
          )}

          {(!isMobile && !isConvergent && tariffServices?.data?.content?.yonth) && <YouthTariff />}

          <AdditionalBalances />

          {isConvergent && (
            <Convergence />
          )}

          <EcoRoaming />
          {!isConvergent && (
            <Accumulators />
          )}
          {externalStore && familyFeatureToggle && (
            <BundlesSharing
              store={externalStore}
              isRestrictedAccess={isRestrictedAccess}
              ctn={ctn}
            />
          )}

          <TariffsBlock />

          {!familyWithBeelineError && !isVisibleFamily && (
            <FamilyWithBeeline />
          )}
          {!isConvergent && (
            <Services />
          )}
          {isPostpaid && (
            <WarrantyPayments />
          )}
        </DataBlocks.Container>

        <ChangeStatusPopups />
        <AccumulatorsSpeedUpPopup />
        {isConvergent && <ValidationPDPopup />}
      </>
    );
  }
}

Home.defaultProps = {
  hasSimTransferRequest: false,
};

Home.propTypes = {
  actions: PropTypes.object,
  partnershipTariff: PropTypes.object,
  profileSummary: PropTypes.object,
  isRestrictedAccess: PropTypes.bool,
  isUserInfoFetching: PropTypes.bool,
  isPostpaid: PropTypes.bool,
  isConvergent: PropTypes.bool,
  isPorted: PropTypes.bool,
  isUserActive: PropTypes.bool,
  bannerData: PropTypes.object,
  tariffServices: PropTypes.object,
  settings: PropTypes.object,
  hasSimTransferRequest: PropTypes.bool,
  sosService: PropTypes.shape({
    data: PropTypes.shape({
      text: PropTypes.string,
      button: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    }),
  }),
  ctn: PropTypes.string,
  familyWithBeelineError: PropTypes.bool,
  isVisibleFamily: PropTypes.bool,
  familyFeatureToggle: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const application = pathOr({}, ['application'], state);
  const settings = pathOr({}, ['settings'], application);
  const home = pathOr({}, ['home'], state);
  const partnershipTariff = state.home?.tariffServices?.data?.yandexPlus?.content;
  const familyWithBeelineError = pathOr(false, ['personalFamily', 'isError'], home);
  const isVisibleFamily = pathOr(false, ['personalFamily', 'personalInfo', 'isVisibleFamily'], home);
  const familyFeatureToggle = home?.familyFeatureToggle?.data;

  return {
    profileSummary: home.profileSummary.data,
    ctn: application.settings.ctn,
    isRestrictedAccess: settings.isRestrictedAccess,
    isPostpaid: application.isPostpaid,
    isConvergent: application.isConvergent,
    isPorted: application.isPorted,
    isUserInfoFetching: home.isUserInfoFetching,
    isUserActive: home?.status?.data?.status === 'Active',
    tariffServices: home.tariffServices,
    sosService: home.sosService,
    bannerData: settings.bannerData,
    hasSimTransferRequest: settings.hasSimTransferRequest,
    settings,
    partnershipTariff,
    familyWithBeelineError,
    isVisibleFamily,
    familyFeatureToggle,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    fetchInitialData,
    setPartnershipTariffStatus,
    fetchPersonalData,
    setUsisResultStep,
    openUsisResultPopup,
    fetchTariffsBlockData,
  }, dispatch),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewAnalytics,
)(Home);
